import { showNotificationToast } from '@genesislcap/foundation-notifications';
import {
  BOND_BIDDING_MESSAGES,
  BOND_BIDDING_TOAST_TITLE,
} from './bond-bidding-messages';

export type BulkToastType = 'success' | 'error' | 'warning';

/**
 * Field type definitions for CSV export formatting.
 */
export const BOND_BIDDING_TIME_FIELDS = ['RECEIVED_TIME', 'DUE_TIME', 'FIRM_TIME'] as const;

export const BOND_BIDDING_DATE_FIELDS = [
  'MATURITY_DATE',
  'PRE_REFUND_DATE',
  'CALL_DATE',
  'WORKOUT_DATE',
  'SETTLEMENT_DATE',
] as const;

export const BOND_BIDDING_NUMERIC_FIELDS = [
  'COUPON_RATE',
  'CALL_PRICE',
  'OUTSTANDING_VOLUME',
  'IOI_SIZE',
  'CALCULATED_BID_PRICE',
  'CALCULATED_YIELD',
] as const;

export const BOND_BIDDING_PRESERVE_AS_TEXT_FIELDS = ['BOND_ID', 'CUSIP', 'BID_ID'] as const;

/**
 * Common toast for bulk operations (download, upload).
 */
export function showBulkToast(
  title: string,
  body: string,
  type: BulkToastType,
  options?: { autoClose?: boolean; closeTimeout?: number }
): void {
  const toast: Record<string, unknown> = { type };
  if (options?.autoClose !== false) {
    toast.autoClose = true;
    toast.closeTimeout = options?.closeTimeout ?? 3000;
  }
  showNotificationToast({ title, body, toast }, 'rapid');
}

/**
 * Builds BOND_ID filter string: BOND_ID == 'id1' || BOND_ID == 'id2'
 * For single ID, no parentheses; for multiple, wrapped in parentheses.
 */
export function buildBondIdFilter(bondIds: string[]): string {
  if (!bondIds.length) return '';
  const filter = bondIds.map((id) => `BOND_ID == '${id}'`).join(' || ');
  return bondIds.length > 1 ? `(${filter})` : filter;
}

/**
 * Builds correlation filter string for upload-backed datasource query.
 */
export function buildCorrelationIdFilter(correlationId: string): string {
  const safeCorrelationId = String(correlationId ?? '').trim();
  if (!safeCorrelationId) return '';
  return `CORRELATION_ID == '${safeCorrelationId}'`;
}

export type BulkBackendOperation = 'download' | 'upload';

export function showBackendUnavailableToast(
  operation: BulkBackendOperation,
  detail?: string
): void {
  const title =
    operation === 'download'
      ? BOND_BIDDING_TOAST_TITLE.downloadFailed
      : BOND_BIDDING_TOAST_TITLE.uploadFailed;
  const body = detail ?? BOND_BIDDING_MESSAGES.download.serverUnavailable;
  showBulkToast(title, body, 'error', { closeTimeout: 6000 });
}

/**
 * Escapes a value for CSV export (handles comma, quote, newline).
 */
export function escapeCsvField(value: string): string {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Triggers CSV download with BOM.
 */
export function triggerCsvDownload(csv: string, filename?: string): void {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download =
    filename ?? `live-activity-export-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
